'use client';

import { useTranslations } from 'next-intl';
import type React from 'react';
import { useId, useState } from 'react';

import ProgramDetailShareButton from '@/app/[locale]/(default-layout)/program/[contentId]/_components/ProgramDetailShareButton';

import ContentTitle from '@/components/ContentTitle';
import ContentDetailImage from '@/components/showing-details/ContentDetailImage';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { IconClose16 } from '@cineamo/ui/src/icons/ic-close';

type ProgramDetailMobileHeaderProps = {
    content: CachedContentData;
    /** Plot, crew and "More about movie". Always in the DOM, only hidden while collapsed. */
    children: React.ReactNode;
    className?: string;
};

/** Compact poster header of the program detail page below `lg`, with the "Show plot & cast" toggle. */
export default function ProgramDetailMobileHeader(props: ProgramDetailMobileHeaderProps): React.JSX.Element {
    const { content, children, className } = props;
    const t = useTranslations();
    const [isExpanded, setIsExpanded] = useState(false);
    const detailsId = useId();

    return (
        <div className={cn('flex flex-col gap-12 py-24', className)}>
            <div className="flex flex-row gap-16">
                <ContentDetailImage
                    content={content}
                    compact
                    priority={false}
                    preloadMedia="(max-width: 1023.98px)"
                />
                <div className="flex flex-col justify-between min-w-0 flex-1 h-[108px]">
                    <ContentTitle
                        content={content}
                        as="h1"
                        showMovieDuration
                        showShare={false}
                        showReleaseBadge={false}
                        classNameTitle="text-headline-20 leading-[28px] tracking-[0.8px] line-clamp-2"
                        classNameMetaLabel="text-body-bold-14"
                        classNameAgeRating="w-[22px] h-[22px]"
                    />
                    <div className="flex flex-row items-center justify-between gap-8">
                        <button
                            type="button"
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                            onClick={() => setIsExpanded((expanded) => !expanded)}
                            className="flex min-w-0 items-center gap-8 whitespace-nowrap py-12 -my-12 text-body-bold-12 text-[var(--primary-color)] cursor-pointer">
                            <span className="truncate">
                                {isExpanded
                                    ? t('program-detail.hide-plot-and-cast')
                                    : t('program-detail.show-plot-and-cast')}
                            </span>
                            {isExpanded && <IconClose16 className="w-16 h-16 shrink-0" />}
                        </button>
                        <ProgramDetailShareButton
                            content={content}
                            variant="text"
                            className="py-12 -my-12 shrink-0"
                        />
                    </div>
                </div>
            </div>
            <div
                id={detailsId}
                className={cn('flex flex-col gap-20 py-8', !isExpanded && 'hidden')}>
                {children}
            </div>
        </div>
    );
}
